import { useEffect, useRef, useState } from "react";
import { CheckCircle, Loader2, ArrowLeft, ExternalLink, ThumbsUp } from "lucide-react";
import { cn } from "@/lib/cn";
import {
  previewSuggestion,
  submitSuggestion,
  type PreviewResponse,
  type SuggestionPayload,
  type SuggestionResponse,
} from "@/lib/api";
import { useTeamsToken } from "@/lib/teamsTokenContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/Button";
import { Textarea } from "@/components/ui/Textarea";
import { Badge } from "@/components/ui/badge";

type Category = SuggestionPayload["category"];

const CATEGORIES: { value: NonNullable<Category>; label: string }[] = [
  { value: "ui", label: "UI / Visual" },
  { value: "answer-quality", label: "Answer Quality" },
  { value: "feature-request", label: "Feature Request" },
  { value: "bug", label: "Bug" },
];

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ModalState = "idle" | "analyzing" | "preview-new" | "preview-dup" | "submitting" | "success" | "error";

export function FeedbackModal({ isOpen, onClose }: FeedbackModalProps) {
  const teamsToken = useTeamsToken();

  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<Category>(undefined);

  const [modalState, setModalState] = useState<ModalState>("idle");
  const [errorMessage, setErrorMessage] = useState("");

  // New-idea preview
  const [polishedDescription, setPolishedDescription] = useState("");
  const [keyPoints, setKeyPoints] = useState<string[]>([]);

  // Duplicate preview
  const [similarIssues, setSimilarIssues] = useState<PreviewResponse["similarIssues"]>([]);

  // Submit result
  const [submitResult, setSubmitResult] = useState<SuggestionResponse | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const polishedRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isOpen) {
      setDescription("");
      setCategory(undefined);
      setModalState("idle");
      setErrorMessage("");
      setPolishedDescription("");
      setKeyPoints([]);
      setSimilarIssues([]);
      setSubmitResult(null);
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  }, [isOpen]);

  useEffect(() => {
    if (modalState !== "success") return;
    const timer = setTimeout(() => onClose(), 4000);
    return () => clearTimeout(timer);
  }, [modalState, onClose]);

  useEffect(() => {
    if (modalState === "preview-new") setTimeout(() => polishedRef.current?.focus(), 50);
  }, [modalState]);

  // ── Step 1: Analyse ──────────────────────────────────────────────────────

  const handleAnalyse = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim() || modalState === "analyzing") return;

    setModalState("analyzing");
    setErrorMessage("");

    try {
      const preview = await previewSuggestion(description.trim(), teamsToken);

      // Always store the polished description — it's used as the upvote
      // comment body on the dup path, not just for new tickets.
      setPolishedDescription(preview.polishedDescription ?? description.trim());
      setKeyPoints(preview.keyPoints ?? []);

      if (preview.similarIssues.length > 0) {
        setSimilarIssues(preview.similarIssues);
        setModalState("preview-dup");
      } else {
        setSimilarIssues([]);
        setModalState("preview-new");
      }
    } catch (err) {
      setModalState("error");
      setErrorMessage(
        err instanceof Error ? err.message : "Something went wrong. Please try again.",
      );
    }
  };

  // ── Step 2a: Submit new ticket (AI-written description) ─────────────────

  const handleSubmitNew = async () => {
    if (!polishedDescription.trim() || modalState === "submitting") return;
    await doSubmit(polishedDescription.trim());
  };

  // ── Step 2b: Submit upvote (duplicate path) ──────────────────────────────

  const handleUpvote = async () => {
    if (modalState === "submitting") return;
    // Send the AI-polished description so the upvote comment is well-written.
    await doSubmit(polishedDescription.trim() || description.trim());
  };

  async function doSubmit(finalDescription: string) {
    setModalState("submitting");
    setErrorMessage("");
    try {
      const result = await submitSuggestion(
        { description: finalDescription, category, timestamp: new Date().toISOString() },
        teamsToken,
      );
      setSubmitResult(result);
      setModalState("success");
    } catch (err) {
      setModalState("error");
      setErrorMessage(
        err instanceof Error ? err.message : "Something went wrong. Please try again.",
      );
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md">

        {/* ── Success ─────────────────────────────────────────── */}
        {modalState === "success" ? (
          <>
            <DialogHeader>
              <DialogTitle>Feedback submitted</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <CheckCircle className="h-10 w-10 text-[var(--success)]" />
              {submitResult?.deduplicated ? (
                <>
                  <p className="text-[14px] font-medium text-text">
                    Someone else already raised this idea — we've added your vote to it.
                  </p>
                  {submitResult.issueId && (
                    <p className="text-[13px] text-text-muted">
                      Ticket{" "}
                      <span className="font-semibold text-text">{submitResult.issueId}</span>
                      {" "}— ask the assistant about it any time.
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p className="text-[14px] font-medium text-text">
                    Thanks — we've logged your feedback and will look into it.
                  </p>
                  {submitResult?.issueId && (
                    <p className="text-[13px] text-text-muted">
                      Ticket{" "}
                      <span className="font-semibold text-text">{submitResult.issueId}</span>
                      {" "}— ask the assistant about it any time.
                    </p>
                  )}
                </>
              )}
              <p className="text-[12px] text-text-muted">This window will close automatically.</p>
            </div>
          </>
        ) : modalState === "preview-dup" || (modalState === "submitting" && similarIssues.length > 0) ? (

          /* ── Duplicate path ──────────────────────────────── */
          <>
            <DialogHeader>
              <DialogTitle>This idea already exists</DialogTitle>
              <DialogDescription>
                The AI found similar ideas already in the tracker. Your vote will be added to the
                best match instead of creating a duplicate.
              </DialogDescription>
            </DialogHeader>

            {/* Matched tickets */}
            <div className="space-y-2">
              {similarIssues.map((issue) => (
                <div
                  key={issue.identifier}
                  className="flex items-start gap-2.5 rounded-lg border border-border bg-surface-muted px-3 py-2.5"
                >
                  <span className="shrink-0 text-[12px] font-semibold text-accent">
                    {issue.identifier}
                  </span>
                  <span className="flex-1 text-[13px] text-text leading-snug">{issue.title}</span>
                  <a
                    href={issue.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 text-text-muted hover:text-text"
                    aria-label={`Open ${issue.identifier} in Linear`}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
              ))}
            </div>

            {/* AI-polished comment that will be added */}
            {polishedDescription && (
              <div>
                <p className="mb-1.5 text-[13px] font-medium text-text">
                  Comment that will be added
                  <span className="ml-1.5 text-[12px] font-normal text-text-muted">(AI-written)</span>
                </p>
                <div className="rounded-lg border border-border bg-surface-muted px-3 py-2.5 text-[13px] text-text leading-relaxed">
                  {polishedDescription}
                </div>
                {keyPoints.length > 0 && (
                  <ul className="mt-2 space-y-1.5 pl-1">
                    {keyPoints.map((point, i) => (
                      <li key={i} className="flex items-start gap-2 text-[13px] text-text">
                        <span className="mt-[3px] h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                        {point}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {errorMessage && (
              <p role="alert" className="rounded-lg border border-[var(--danger)]/30 bg-[var(--danger)]/5 px-3 py-2 text-[13px] text-[var(--danger)]">
                {errorMessage}
              </p>
            )}

            <div className="flex justify-between gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setModalState("idle")}
                disabled={modalState === "submitting"}
                className="gap-1.5"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Edit
              </Button>
              <div className="flex gap-2">
                <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={modalState === "submitting"}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={handleUpvote}
                  disabled={modalState === "submitting"}
                  className="gap-1.5"
                >
                  {modalState === "submitting" ? (
                    <><Loader2 className="h-3.5 w-3.5 animate-spin" />Adding vote…</>
                  ) : (
                    <><ThumbsUp className="h-3.5 w-3.5" />Add my vote</>
                  )}
                </Button>
              </div>
            </div>
          </>
        ) : modalState === "preview-new" || (modalState === "submitting" && similarIssues.length === 0) ? (

          /* ── New-idea preview ────────────────────────────── */
          <>
            <DialogHeader>
              <DialogTitle>Review before submitting</DialogTitle>
              <DialogDescription>
                Here's how the AI wrote up your feedback. Edit if needed, then submit.
              </DialogDescription>
            </DialogHeader>

            {/* Polished description — top, editable */}
            <div>
              <p className="mb-1.5 text-[13px] font-medium text-text">Description</p>
              <Textarea
                ref={polishedRef}
                value={polishedDescription}
                onChange={(e) => setPolishedDescription(e.target.value)}
                rows={4}
                maxLength={2000}
                disabled={modalState === "submitting"}
                className="rounded-lg border border-border bg-surface-muted px-3 py-2 text-[13.5px] placeholder:text-text-muted/60 focus:border-accent focus:ring-1 focus:ring-accent disabled:opacity-60"
              />
            </div>

            {/* Key points — below */}
            {keyPoints.length > 0 && (
              <div>
                <p className="mb-2 text-[13px] font-medium text-text">Key points</p>
                <ul className="space-y-1.5">
                  {keyPoints.map((point, i) => (
                    <li key={i} className="flex items-start gap-2 text-[13px] text-text">
                      <span className="mt-[3px] h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {errorMessage && (
              <p role="alert" className="rounded-lg border border-[var(--danger)]/30 bg-[var(--danger)]/5 px-3 py-2 text-[13px] text-[var(--danger)]">
                {errorMessage}
              </p>
            )}

            <div className="flex justify-between gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setModalState("idle")}
                disabled={modalState === "submitting"}
                className="gap-1.5"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Edit
              </Button>
              <div className="flex gap-2">
                <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={modalState === "submitting"}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={handleSubmitNew}
                  disabled={!polishedDescription.trim() || modalState === "submitting"}
                  className="gap-1.5"
                >
                  {modalState === "submitting" ? (
                    <><Loader2 className="h-3.5 w-3.5 animate-spin" />Submitting…</>
                  ) : (
                    "Submit this"
                  )}
                </Button>
              </div>
            </div>
          </>
        ) : (

          /* ── Idle / Analysing / Error ─────────────────────── */
          <>
            <DialogHeader>
              <DialogTitle>Share feedback</DialogTitle>
              <DialogDescription>
                Tell us what's on your mind — the AI will write it up properly before it's logged.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleAnalyse} noValidate>
              <div className="mb-4">
                <p className="mb-2 text-[13px] font-medium text-text">
                  Category
                  <span className="ml-1.5 text-[12px] font-normal text-text-muted">(optional)</span>
                </p>
                <div className="flex flex-wrap gap-2">
                  {CATEGORIES.map(({ value, label }) => (
                    <Badge
                      key={value}
                      variant="outline"
                      onClick={() => setCategory(category === value ? undefined : value)}
                      className={cn(
                        "cursor-pointer px-3 py-1 text-[12.5px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
                        modalState === "analyzing" && "pointer-events-none opacity-50",
                        category === value
                          ? "border-accent bg-accent/10 text-accent"
                          : "border-border text-text-muted hover:border-accent/50 hover:text-text",
                      )}
                    >
                      {label}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="mb-5">
                <label
                  htmlFor="feedback-description"
                  className="mb-1.5 block text-[13px] font-medium text-text"
                >
                  What's on your mind?
                  <span aria-hidden className="ml-1 text-[var(--danger)]">*</span>
                </label>
                <Textarea
                  id="feedback-description"
                  ref={textareaRef}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe what you noticed, what you'd like to see, or what went wrong…"
                  maxLength={2000}
                  rows={4}
                  required
                  disabled={modalState === "analyzing"}
                  className="rounded-lg border border-border bg-surface-muted px-3 py-2 text-[13.5px] placeholder:text-text-muted/60 focus:border-accent focus:ring-1 focus:ring-accent disabled:opacity-60"
                />
              </div>

              {modalState === "error" && (
                <p role="alert" className="mb-4 rounded-lg border border-[var(--danger)]/30 bg-[var(--danger)]/5 px-3 py-2 text-[13px] text-[var(--danger)]">
                  {errorMessage}
                </p>
              )}

              <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={modalState === "analyzing"}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  disabled={!description.trim() || modalState === "analyzing"}
                  className="gap-1.5"
                >
                  {modalState === "analyzing" ? (
                    <><Loader2 className="h-3.5 w-3.5 animate-spin" />Analysing…</>
                  ) : (
                    "Preview & submit"
                  )}
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
